import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { message } from 'antd';
import { requestMagicLink } from 'api/auth';
import { useAnalytics } from 'analytics';
import UnauthLayout from 'components/UnauthLayout';
import ForgotPasswordForm from 'pages/forgot-password/ForgotPasswordForm';
import { Button, Typography } from 'ui-kit';
import './MagicLinkPage.scss';

const RESEND_COOLDOWN_SECONDS = 60;

export default function MagicLinkRequestPage() {
  const location = useLocation<{ email?: string }>();
  const capture = useAnalytics();
  const [sentEmail, setSentEmail] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) {
      return undefined;
    }

    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const sendLink = (email: string) =>
    requestMagicLink({ email })
      .then(() => {
        capture('magic_link_requested', {});
        setSentEmail(email);
        setCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .catch((error) => {
        // The sdk drops the status code and rejects a 429 as its bare `{ detail }` body, so
        // the text DRF puts in `detail` is the only way to tell a throttled request apart.
        const isThrottled = String(error?.detail ?? '').startsWith('Request was throttled');

        message.error(
          isThrottled
            ? 'Too many requests. Try again later.'
            : error?.message || 'Something went wrong. Try again.',
        );
      });

  const handleResend = () => {
    setIsResending(true);
    sendLink(sentEmail).finally(() => setIsResending(false));
  };

  if (sentEmail) {
    return (
      <UnauthLayout compact>
        <div className="MagicLinkPage">
          <div className="MagicLinkPage-copy">
            <Typography variant="h5">Check your inbox</Typography>
            <Typography variant="body2" color="textGray">
              We sent a link to <b>{sentEmail}</b>.
              <br />
              It expires in 15 minutes and works once.
            </Typography>
          </div>
          <div className="MagicLinkPage-actions">
            <Button
              variant="outlined"
              fullWidth
              disabled={cooldown > 0}
              loading={isResending}
              onClick={handleResend}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend link'}
            </Button>
            <Button to="/login/" variant="text" color="primary" size="small" fullWidth>
              Back to sign in
            </Button>
          </div>
        </div>
      </UnauthLayout>
    );
  }

  return (
    <UnauthLayout compact>
      <div className="MagicLinkPage">
        <div className="MagicLinkPage-copy">
          <Typography variant="h5">Email me a sign-in link</Typography>
          <Typography variant="body2" color="textGray">
            We will email you a link to sign in.
            <br />
            Co-Hosts sign in with a password.
          </Typography>
        </div>
        <div className="MagicLinkPage-actions">
          <div className="MagicLinkPage-form">
            <ForgotPasswordForm
              initialValues={{ email: location.state?.email ?? '' }}
              onSubmit={({ email }) => sendLink(email)}
              submitLabel="Send sign-in link"
            />
          </div>
          <Button to="/login/" variant="text" color="primary" size="small" fullWidth>
            Back to sign in
          </Button>
        </div>
      </div>
    </UnauthLayout>
  );
}
